<template>
  <div class="coupon-template-list-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-tickets"></i>
              优惠券模板管理
            </h1>
            <p class="page-subtitle">管理和配置所有优惠券模板，控制发放策略</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button type="primary" size="medium" @click="handleCreate">
            <i class="el-icon-plus"></i> 新增模板
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 筛选区域 -->
      <div class="filter-section">
        <el-card class="filter-card" shadow="hover">
          <div class="filter-form">
            <el-row :gutter="20" align="middle">
              <el-col :span="8">
                <div class="form-item">
                  <label class="form-label">模板名称</label>
                  <el-input
                    v-model="queryForm.keyword"
                    placeholder="请输入模板名称进行搜索"
                    clearable
                    size="medium"
                    @keyup.enter.native="handleSearch"
                  >
                    <i slot="prefix" class="el-input__icon el-icon-search"></i>
                  </el-input>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="form-item">
                  <label class="form-label">模板状态</label>
                  <el-select 
                    v-model="queryForm.is_active" 
                    placeholder="请选择状态" 
                    clearable 
                    size="medium"
                    style="width: 100%"
                  >
                    <el-option label="启用中" :value="true">
                      <span style="color: #67c23a">
                        <i class="el-icon-success"></i> 启用中
                      </span>
                    </el-option>
                    <el-option label="已禁用" :value="false">
                      <span style="color: #f56c6c">
                        <i class="el-icon-error"></i> 已禁用
                      </span>
                    </el-option>
                  </el-select>
                </div>
              </el-col>
              <el-col :span="10">
                <div class="form-actions">
                  <el-button type="primary" size="medium" @click="handleSearch" :loading="tableLoading">
                    <i class="el-icon-search"></i> 搜索
                  </el-button>
                  <el-button size="medium" @click="resetSearch">
                    <i class="el-icon-refresh"></i> 重置
                  </el-button>
                  <el-button size="medium" @click="fetchData">
                    <i class="el-icon-refresh"></i> 刷新
                  </el-button>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-card>
      </div>

      <!-- 数据统计 -->
      <div class="stats-section" v-if="templateList.length > 0">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon">
                <i class="el-icon-tickets"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ templateList.length }}</div>
                <div class="stat-label">模板总数</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon active">
                <i class="el-icon-success"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ templateList.filter(t => t.is_active).length }}</div>
                <div class="stat-label">启用中</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon disabled">
                <i class="el-icon-error"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ templateList.filter(t => !t.is_active).length }}</div>
                <div class="stat-label">已禁用</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon total">
                <i class="el-icon-s-data"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ templateList.reduce((sum, t) => sum + (t.issued_quantity || 0), 0) }}</div>
                <div class="stat-label">总发放量</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 模板列表 -->
      <div class="table-section">
        <el-card class="table-card" shadow="hover">
          <div slot="header" class="table-header">
            <span class="table-title">
              <i class="el-icon-document"></i>
              模板列表
            </span>
            <span class="table-count">共 {{ total }} 条记录</span>
          </div>
          
          <el-table
            v-loading="tableLoading"
            :data="templateList"
            style="width: 100%"
            :row-class-name="tableRowClassName"
            @row-click="handleRowClick"
            :header-cell-style="{ background: '#fafbfc', color: '#606266', fontWeight: '600' }"
          >
            <el-table-column label="模板信息" min-width="300">
              <template slot-scope="scope">
                <div class="template-info">
                  <div class="template-main">
                    <div class="template-name">
                      <i class="el-icon-tickets template-icon"></i>
                      {{ scope.row.coupon_name }}
                    </div>
                    <div class="template-desc">{{ scope.row.description || '暂无描述' }}</div>
                  </div>
                  <div class="template-time">
                    <i class="el-icon-time"></i>
                    {{ formatDate(scope.row.created_at) }}
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="折扣力度" width="120" align="center">
              <template slot-scope="scope">
                <div class="discount-cell">
                  <div class="discount-badge">
                    {{ formatDiscountRate(scope.row.discount_rate) }}
                  </div>
                  <div class="threshold-text">
                    {{ scope.row.min_amount > 0 ? `满${scope.row.min_amount}元` : '无门槛' }}
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="规则信息" width="180" align="center">
              <template slot-scope="scope">
                <div class="rules-info">
                  <div class="rule-line">
                    <i class="el-icon-time" style="color: #409eff;"></i>
                    <span>{{ scope.row.valid_days }}天有效</span>
                  </div>
                  <div class="rule-line">
                    <i class="el-icon-user" style="color: #67c23a;"></i>
                    <span>限用{{ scope.row.usage_limit }}次</span>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="发放统计" width="120" align="center">
              <template slot-scope="scope">
                <div class="stats-info">
                  <div class="stats-line">
                    <span class="stats-label">总量:</span>
                    <span class="stats-value primary">{{ scope.row.total_quantity === -1 ? '不限' : scope.row.total_quantity }}</span>
                  </div>
                  <div class="stats-line">
                    <span class="stats-label">已发:</span>
                    <span class="stats-value success">{{ scope.row.issued_quantity || 0 }}</span>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag 
                  :type="scope.row.is_active ? 'success' : 'danger'"
                  :effect="scope.row.is_active ? 'dark' : 'plain'"
                  size="medium"
                  @click="handleToggleStatus(scope.row)"
                  style="cursor: pointer; user-select: none;"
                  :title="scope.row.is_active ? '点击禁用' : '点击启用'"
                >
                  <i :class="scope.row.is_active ? 'el-icon-check' : 'el-icon-close'"></i>
                  {{ scope.row.is_active ? '已启用' : '已禁用' }}
                </el-tag>
              </template>
            </el-table-column>

        <el-table-column prop="created_at" label="创建时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>

            <el-table-column label="操作" width="180" fixed="right" align="center">
              <template slot-scope="scope">
                <div class="action-buttons">
                  <el-button 
                    size="mini" 
                    icon="el-icon-view" 
                    circle
                    @click.stop="handleDetail(scope.row)"
                    title="查看详情"
                  />
                  <el-button 
                    size="mini" 
                    type="primary" 
                    icon="el-icon-edit" 
                    circle
                    @click.stop="handleEdit(scope.row)"
                    title="编辑模板"
                  />
                  <el-button 
                    size="mini" 
                    type="success"
                    icon="el-icon-s-promotion" 
                    circle
                    @click.stop="handleIssue(scope.row)"
                    title="发放优惠券"
                  />
                </div>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </div>

      <!-- 分页 -->
      <div class="pagination-section">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="queryForm.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="queryForm.page_size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          background
        />
      </div>
    </div>

    <!-- 模板详情对话框 -->
    <el-dialog title="模板详情" :visible.sync="detailDialogVisible" width="600px">
      <div v-if="selectedTemplate" class="template-detail">
        <el-row :gutter="20">
          <el-col :span="12">
            <div class="detail-item">
              <label>模板名称：</label>
              <span>{{ selectedTemplate.coupon_name }}</span>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="detail-item">
              <label>折扣率：</label>
              <span class="discount-rate">{{ formatDiscountRate(selectedTemplate.discount_rate) }}</span>
            </div>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <div class="detail-item">
              <label>使用门槛：</label>
              <span>{{ selectedTemplate.min_amount > 0 ? `满${selectedTemplate.min_amount}元` : '无门槛' }}</span>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="detail-item">
              <label>有效天数：</label>
              <span>{{ selectedTemplate.valid_days }}天</span>
            </div>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <div class="detail-item">
              <label>使用次数：</label>
              <span>每人{{ selectedTemplate.usage_limit }}次</span>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="detail-item">
              <label>叠加使用：</label>
              <el-tag :type="selectedTemplate.is_stackable ? 'success' : 'info'" size="small">
                {{ selectedTemplate.is_stackable ? '可叠加' : '不可叠加' }}
              </el-tag>
            </div>
          </el-col>
        </el-row>

        <div class="detail-item">
          <label>适用商品：</label>
          <span>{{ selectedTemplate.applicable_products ? '指定商品' : '全部商品' }}</span>
        </div>

        <div class="detail-item">
          <label>模板说明：</label>
          <p>{{ selectedTemplate.description || '暂无说明' }}</p>
        </div>

        <div class="statistics-section">
          <h4>发放统计</h4>
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="stat-item">
                <div class="stat-value">{{ selectedTemplate.total_quantity === -1 ? '不限' : selectedTemplate.total_quantity }}</div>
                <div class="stat-label">总发放量</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item">
                <div class="stat-value">{{ selectedTemplate.issued_quantity || 0 }}</div>
                <div class="stat-label">已发放</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item">
                <div class="stat-value">
                  {{ selectedTemplate.total_quantity === -1 ? '不限' : Math.max(0, selectedTemplate.total_quantity - (selectedTemplate.issued_quantity || 0)) }}
                </div>
                <div class="stat-label">剩余</div>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEdit(selectedTemplate)">编辑模板</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCouponTemplates, updateCouponTemplate, formatDiscountRate } from '@/api/coupon'

export default {
  name: 'CouponTemplateList',
  data() {
    return {
      // 查询表单
      queryForm: {
        page: 1,
        page_size: 20,
        keyword: '',
        is_active: null
      },
      
      // 列表数据
      templateList: [],
      total: 0,
      tableLoading: false,
      toggleLoading: false,
      
      // 对话框
      detailDialogVisible: false,
      selectedTemplate: null
    }
  },
  
  created() {
    this.fetchData()
  },
  
  methods: {
    // 获取模板列表
    async fetchData() {
      this.tableLoading = true
      try {
        const response = await getCouponTemplates(this.queryForm)
        console.log('API Response:', response) // 调试日志
        
        // 根据实际API响应结构调整数据获取
        if (response.data && response.data.list) {
          this.templateList = response.data.list
          this.total = response.data.pagination ? response.data.pagination.total : response.data.list.length
        } else if (response.data && Array.isArray(response.data)) {
          // 如果直接返回数组格式
          this.templateList = response.data
          this.total = response.data.length
        } else {
          console.warn('Unexpected API response format:', response)
          this.templateList = []
          this.total = 0
        }
        
        console.log('Template List:', this.templateList) // 调试模板数据
      } catch (error) {
        console.error('获取模板列表失败:', error)
        this.$message.error('获取模板列表失败: ' + (error.message || '网络错误'))
      } finally {
        this.tableLoading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.queryForm.page = 1
      this.fetchData()
    },
    
    // 重置搜索
    resetSearch() {
      this.queryForm = {
        page: 1,
        page_size: 20,
        keyword: '',
        is_active: null
      }
      this.fetchData()
    },
    
    // 分页大小改变
    handleSizeChange(size) {
      this.queryForm.page_size = size
      this.queryForm.page = 1
      this.fetchData()
    },
    
    // 页码改变
    handleCurrentChange(page) {
      this.queryForm.page = page
      this.fetchData()
    },
    
    // 新增模板
    handleCreate() {
      this.$router.push('/admin/coupons/templates/create')
    },
    
    // 查看详情
    handleDetail(template) {
      this.selectedTemplate = template
      this.detailDialogVisible = true
    },
    
    // 编辑模板
    handleEdit(template) {
      console.log('Edit template:', template) // 调试模板对象
      const templateId = template.id || template.template_id
      if (!templateId) {
        this.$message.error('模板ID不存在，无法编辑')
        console.error('Template object:', template)
        return
      }
      this.$router.push(`/admin/coupons/templates/${templateId}/edit`)
    },
    
    // 发放优惠券
    handleIssue(template) {
      console.log('Issue template:', template) // 调试模板对象
      const templateId = template.id || template.template_id
      if (!templateId) {
        this.$message.error('模板ID不存在，无法发放')
        console.error('Template object:', template)
        return
      }
      this.$router.push({
        name: 'CouponIssue',
        query: { template_id: templateId }
      })
    },
    
    // 切换状态
    async handleToggleStatus(template) {
      const action = template.is_active ? '禁用' : '启用'
      const templateId = template.id || template.template_id
      
      if (!templateId) {
        this.$message.error('模板ID不存在，无法切换状态')
        console.error('Template object:', template)
        return
      }
      
      this.toggleLoading = true
      try {        
        await updateCouponTemplate(templateId, {
          is_active: !template.is_active
        })
        
        this.$message.success(`${action}成功`)
        // 不重新获取数据，直接更新本地状态
        // this.fetchData()
      } catch (error) {
        // 如果出错，恢复原状态
        template.is_active = !template.is_active
        this.$message.error(`${action}失败: ` + (error.message || '网络错误'))
        console.error('Toggle status error:', error)
      } finally {
        this.toggleLoading = false
      }
    },

    // 表格行点击
    handleRowClick(row) {
      // 可以在这里添加行点击逻辑，比如高亮选中等
    },

    // 表格行样式
    tableRowClassName({ row, rowIndex }) {
      if (!row.is_active) {
        return 'disabled-row'
      }
      return ''
    },
    
    // 格式化折扣率
    formatDiscountRate,
    
    // 格式化日期
    formatDate(date) {
      return new Date(date).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.coupon-template-list-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

/* 主要内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 25px;
}

.filter-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.filter-form {
  padding: 10px 0;
}

.form-item {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  margin-bottom: 8px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
}

/* 统计卡片 */
.stats-section {
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f3f4;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
}

.stat-icon::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  background: linear-gradient(135deg, #409eff, #36a3f7);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
}

.stat-icon i {
  color: #409eff;
  z-index: 1;
}

.stat-icon.active::before {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stat-icon.active i {
  color: #67c23a;
}

.stat-icon.disabled::before {
  background: linear-gradient(135deg, #f56c6c, #f78989);
}

.stat-icon.disabled i {
  color: #f56c6c;
}

.stat-icon.total::before {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
}

.stat-icon.total i {
  color: #e6a23c;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 表格区域 */
.table-section {
  margin-bottom: 25px;
}

.table-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-count {
  font-size: 14px;
  color: #909399;
}

/* 表格内容样式 */
.template-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
}

.template-main {
  flex: 1;
}

.template-name {
  font-weight: 600;
  color: #303133;
  font-size: 16px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.template-icon {
  color: #409eff;
}

.template-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 0;
}

.template-time {
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

/* 折扣信息样式 */
.discount-cell {
  text-align: center;
}

.discount-badge {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 25px;
  padding: 8px 20px;
  color: white;
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 8px;
  display: inline-block;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.threshold-text {
  font-size: 12px;
  color: #666;
}

/* 规则信息样式 */
.rules-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
}

.rule-line {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: rgba(64, 158, 255, 0.1);
  border-radius: 12px;
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
  min-width: fit-content;
}

.rule-line:nth-child(2) {
  background: rgba(103, 194, 58, 0.1);
}

/* 发放统计样式 */
.stats-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}

.stats-line {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  white-space: nowrap;
}

.stats-label {
  color: #909399;
  font-size: 11px;
}

.stats-value {
  font-weight: 600;
  font-size: 12px;
}

.stats-value.primary {
  color: #409eff;
}

.stats-value.success {
  color: #67c23a;
}

/* 清理旧的发放统计样式，已被新样式替代 */

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.action-buttons .el-button {
  margin: 0;
}

/* 分页区域 */
.pagination-section {
  display: flex;
  justify-content: center;
  padding: 30px 0;
}

/* 表格行状态 */
:deep(.disabled-row) {
  background-color: #fafafa !important;
  opacity: 0.7;
}

:deep(.disabled-row:hover > td) {
  background-color: #f5f5f5 !important;
}

/* 表格样式优化 */
:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
}

:deep(.el-table th) {
  font-weight: 600;
  text-align: center;
}

:deep(.el-table td) {
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

:deep(.el-table--striped .el-table__body tr.el-table__row--striped td) {
  background: #fafbfc;
}

:deep(.el-table__body tr:hover > td) {
  background-color: #f5f7fa !important;
}

/* 模板详情对话框 */
.template-detail {
  padding: 20px 0;
}

.detail-item {
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
}

.detail-item label {
  font-weight: bold;
  color: #606266;
  width: 100px;
  flex-shrink: 0;
}

.discount-rate {
  color: #e74c3c;
  font-weight: bold;
  font-size: 16px;
}

.statistics-section {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.statistics-section h4 {
  margin-bottom: 15px;
  color: #303133;
}

.stat-item {
  text-align: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409eff;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: #909399;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-content {
    padding: 0 20px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
  
  .stats-section .el-row {
    display: flex;
    flex-wrap: wrap;
  }
  
  .stats-section .el-col {
    margin-bottom: 15px;
  }
  
  .template-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .template-time {
    align-self: flex-end;
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .main-content {
    padding: 0 15px;
  }
  
  .filter-form .el-col {
    margin-bottom: 15px;
  }
  
  .form-actions {
    justify-content: center;
  }
  
  .stats-section .el-col {
    flex: 0 0 50%;
    max-width: 50%;
  }
  
  .stat-card {
    padding: 20px 16px;
    gap: 12px;
  }
  
  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 22px;
  }
  
  .stat-icon::before {
    border-radius: 18px;
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  .action-buttons {
    gap: 4px;
  }
  
  .action-buttons .el-button {
    font-size: 12px;
  }
  
  .rules-info {
    gap: 4px;
  }
  
  .rule-line {
    padding: 2px 6px;
    font-size: 11px;
  }
  
  .stats-info {
    gap: 2px;
  }
  
  .stats-line {
    gap: 2px;
  }
  
  .stats-label, .stats-value {
    font-size: 10px;
  }
  
  /* 移动端状态标签优化 */
  .el-tag {
    font-size: 11px;
    padding: 0 6px;
  }
}

@media (max-width: 480px) {
  .stats-section .el-col {
    flex: 0 0 100%;
    max-width: 100%;
  }
  
  .action-buttons .el-button {
    padding: 6px;
    min-width: 36px;
  }
}
</style> 